/**
 * Admin 레이아웃: primary drawer 모바일 토글 + drilldown 스택 머신 + 헤더 드롭다운.
 *
 * Drilldown 동작:
 *   - 각 `.admin-drawer__panel` 은 단일 컬럼 안에서 transform 으로 좌우 슬라이드.
 *   - `[data-drawer-drill="<panel>"]` 클릭 → 그 패널로 push (is-active).
 *   - `[data-drawer-back]` 클릭 또는 ESC → 부모 패널로 pop.
 *   - 초기 로드 시 nav 의 `data-drawer-initial-panel` 값을 보고 그 깊이까지 애니메이션 없이 점프.
 *
 * 트리 모드(`admin.drawer.mode = tree`) 에서는 drilldown 패널이 없고 details/summary 가 직접 동작.
 */

interface DrillState {
    nav: HTMLElement;
    stack: string[];
    panels: Map<string, HTMLElement>;
}

interface FlyoutState {
    nav: HTMLElement;
    panels: Map<string, HTMLElement>;
    openKey: string | null;
    persistent: boolean;
    floating: boolean;
}

export function initAdminLayoutDrawer(): void {
    const body       = document.getElementById("admin-body");
    const overlay    = document.getElementById("admin-drawer-overlay");
    const toggleBtns = document.querySelectorAll<HTMLElement>("[data-admin-drawer-toggle]");
    const nav        = document.getElementById("admin-drawer");

    // ── Primary drawer (모바일 토글) ───────────────────────────────────────
    function closeDrawer(): void {
        body?.classList.remove("admin--drawer-open");
        toggleBtns.forEach((btn) => {
            btn.setAttribute("aria-expanded", "false");
            btn.setAttribute("aria-label", "메뉴 열기");
        });
        overlay?.setAttribute("aria-hidden", "true");
    }

    function openDrawer(): void {
        body?.classList.add("admin--drawer-open");
        toggleBtns.forEach((btn) => {
            btn.setAttribute("aria-expanded", "true");
            btn.setAttribute("aria-label", "메뉴 닫기");
        });
        overlay?.setAttribute("aria-hidden", "false");
    }

    toggleBtns.forEach((btn) => btn.addEventListener("click", () => {
        if (body?.classList.contains("admin--drawer-open")) closeDrawer();
        else openDrawer();
    }));

    overlay?.addEventListener("click", closeDrawer);

    // ── Drilldown 스택 머신 ────────────────────────────────────────────────
    const drill = initDrilldown(nav);

    if (drill !== null) {
        // drill 버튼: 자식 패널로 push
        drill.nav.addEventListener("click", (e: Event) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            const drillBtn = target.closest<HTMLButtonElement>("[data-drawer-drill]");
            if (drillBtn) {
                const panelId = drillBtn.dataset.drawerDrill;
                if (panelId) {
                    pushPanel(drill, panelId);
                }
                return;
            }
            const backBtn = target.closest<HTMLButtonElement>("[data-drawer-back]");
            if (backBtn) {
                popPanel(drill);
            }
        });

        document.addEventListener("keydown", (e) => {
            if (e.key !== "Escape") return;
            if (drill.stack.length > 1) {
                popPanel(drill);
            } else if (body?.classList.contains("admin--drawer-open")) {
                closeDrawer();
            }
        });

        // 모바일: leaf 링크 클릭 시 primary drawer 자동 닫힘 (기존 동작)
        drill.nav.querySelectorAll<HTMLAnchorElement>("a.admin-drawer__leaf").forEach((a) => {
            a.addEventListener("click", () => {
                if (window.innerWidth <= 767) closeDrawer();
            });
        });
    }

    // ── Nested 플라이아웃 (좌측 컬럼 + 측면 패널) ──────────────────────────
    const flyout = initNestedFlyout(nav);

    if (flyout !== null) {
        // 초기 SSR 열림 상태를 nav 에 반영 → 본문 dim 동기화(형제 선택자 hook)
        if (flyout.openKey) flyout.nav.setAttribute("data-flyout-active", "");

        // 토글 버튼(좌컬럼 group / flyout 헤더 닫기) 클릭 → 해당 flyout 토글
        flyout.nav.addEventListener("click", (e: Event) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            const toggle = target.closest<HTMLElement>("[data-flyout-toggle]");
            if (toggle) {
                e.preventDefault();
                const key = toggle.dataset.flyoutToggle;
                if (key) toggleFlyout(flyout, key);
            }
        });

        if (flyout.floating) {
            bindFloatingHover(flyout);
        }

        // ESC: 열린 flyout 닫고 토글로 focus 복귀. 없으면 모바일 drawer 닫기.
        document.addEventListener("keydown", (e) => {
            if (e.key !== "Escape") return;
            if (flyout.floating && closeDeepestMobileSubmenu(flyout)) {
                return;
            }
            if (flyout.floating && closeDeepestFloatingSubmenu(flyout)) {
                return;
            }
            if (flyout.openKey) {
                const key = flyout.openKey;
                closeFlyout(flyout, key);
                findToggle(flyout, key)?.focus();
            } else if (body?.classList.contains("admin--drawer-open")) {
                closeDrawer();
            }
        });

        // 바깥(nav 밖) 클릭 → 열린 flyout 닫기
        document.addEventListener("click", (e) => {
            if (flyout.persistent) return;
            if (!flyout.openKey) return;
            const target = e.target as Node | null;
            if (target && !flyout.nav.contains(target)) {
                closeFlyout(flyout, flyout.openKey);
            }
        });

        // 모바일: leaf 클릭 시 primary drawer 자동 닫힘
        flyout.nav.querySelectorAll<HTMLAnchorElement>("a.admin-drawer__leaf").forEach((a) => {
            a.addEventListener("click", () => {
                if (window.innerWidth <= 767) closeDrawer();
            });
        });

        window.addEventListener("resize", () => {
            if (!flyout.floating || !flyout.openKey) return;
            positionFloatingFlyout(flyout, flyout.openKey);
            positionOpenFloatingSubmenus(flyout);
        });

        window.addEventListener("scroll", () => {
            if (!flyout.floating || !flyout.openKey) return;
            positionFloatingFlyout(flyout, flyout.openKey);
            positionOpenFloatingSubmenus(flyout);
        }, true);
    }

    // ── 헤더 드롭다운 외부 클릭 닫기 ─────────────────────────────────────
    document.addEventListener("click", (e) => {
        const target = e.target as Node | null;

        const userDropdown = document.querySelector<HTMLDetailsElement>("[data-admin-user-dropdown]");
        if (userDropdown && !userDropdown.contains(target)) {
            userDropdown.removeAttribute("open");
        }

        document.querySelectorAll<HTMLDetailsElement>("[data-admin-menu-dropdown]").forEach((d) => {
            if (!d.contains(target)) d.removeAttribute("open");
        });
    });

    // 헤더 드롭다운 상호 배타
    const menuDropdowns = document.querySelectorAll<HTMLDetailsElement>("[data-admin-menu-dropdown]");
    menuDropdowns.forEach((d) => {
        d.addEventListener("toggle", () => {
            if (!d.open) return;
            menuDropdowns.forEach((other) => { if (other !== d) other.removeAttribute("open"); });
            document.querySelector<HTMLDetailsElement>("[data-admin-user-dropdown]")?.removeAttribute("open");
        });
    });
    document.querySelector<HTMLDetailsElement>("[data-admin-user-dropdown]")?.addEventListener("toggle", function () {
        if (!(this as HTMLDetailsElement).open) return;
        menuDropdowns.forEach((d) => d.removeAttribute("open"));
    });
}

// ─ Drilldown helpers ─────────────────────────────────────────────────────

/**
 * drilldown 모드 nav 초기화.
 * 트리 모드 nav 면 null 반환 (= drilldown 로직 비활성).
 */
function initDrilldown(nav: HTMLElement | null): DrillState | null {
    if (!nav || nav.dataset.drawerMode !== "drilldown") return null;

    const panels = new Map<string, HTMLElement>();
    nav.querySelectorAll<HTMLElement>(".admin-drawer__panel").forEach((p) => {
        const id = p.dataset.drawerPanel;
        if (id) panels.set(id, p);
    });
    if (panels.size === 0) return null;

    // 초기 스택 구성: data-drawer-initial-panel = "content.stores" → ['root','content','content.stores']
    const initial = nav.dataset.drawerInitialPanel ?? "root";
    const parts = initial.split(".");
    const stack: string[] = ["root"];
    if (initial !== "root") {
        for (let i = 1; i <= parts.length; i++) {
            const id = parts.slice(0, i).join(".");
            if (panels.has(id) && id !== "root") stack.push(id);
        }
    }

    const state: DrillState = { nav, stack, panels };
    // 애니메이션 없이 즉시 점프
    setActivePanel(state, stack[stack.length - 1], { animate: false });
    return state;
}

function setActivePanel(state: DrillState, target: string, opts: { animate?: boolean } = {}): void {
    const animate = opts.animate !== false;
    state.panels.forEach((p, id) => {
        const isActive = id === target;
        const isPrev = state.stack.includes(id) && id !== target;
        p.classList.toggle("is-no-animate", !animate);
        p.classList.toggle("is-active", isActive);
        p.classList.toggle("is-prev", isPrev);
        p.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
    // 다음 프레임에 is-no-animate 제거 (이후 동작은 애니메이션)
    if (!animate) {
        requestAnimationFrame(() => {
            state.panels.forEach((p) => p.classList.remove("is-no-animate"));
        });
    }
    // 활성 패널의 첫 포커스 가능 요소로 포커스 (drill/back 동작 시)
    if (animate) {
        const active = state.panels.get(target);
        const first = active?.querySelector<HTMLElement>(
            "button[data-drawer-back], button[data-drawer-drill], a.admin-drawer__leaf"
        );
        if (first) {
            // 슬라이드 후 포커스 (애니메이션 길이 직후)
            window.setTimeout(() => first.focus({ preventScroll: true }), 250);
        }
    }
}

function pushPanel(state: DrillState, panelId: string): void {
    if (!state.panels.has(panelId)) return;
    if (state.stack[state.stack.length - 1] === panelId) return;
    state.stack.push(panelId);
    setActivePanel(state, panelId);
}

function popPanel(state: DrillState): void {
    if (state.stack.length <= 1) return;
    state.stack.pop();
    const target = state.stack[state.stack.length - 1];
    // pop 은 prev → active 전환: 현재 활성 패널을 오른쪽 밖으로, 부모를 가운데로
    state.panels.forEach((p, id) => {
        const isActive = id === target;
        const isPrev = state.stack.includes(id) && !isActive;
        // 현재 active 였던 (이제 떠나는) 패널은 오른쪽으로 돌아감 — translateX(100%) 가 기본 transform
        p.classList.toggle("is-active", isActive);
        p.classList.toggle("is-prev", isPrev);
        p.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
    // 포커스 — 백 버튼 또는 첫 항목
    const active = state.panels.get(target);
    const first = active?.querySelector<HTMLElement>(
        "button[data-drawer-back], button[data-drawer-drill], a.admin-drawer__leaf"
    );
    if (first) window.setTimeout(() => first.focus({ preventScroll: true }), 250);
}

// ─ Nested 플라이아웃 helpers ──────────────────────────────────────────────

/**
 * nested 모드 nav 초기화. 다른 모드 nav 면 null.
 * SSR 이 현재 라우트 부모 flyout 에 이미 data-flyout-open 을 부여하므로 초기 open 은 그대로 둔다.
 */
function initNestedFlyout(nav: HTMLElement | null): FlyoutState | null {
    if (!nav || !["nested", "figma-floating", "side-expanded"].includes(nav.dataset.drawerMode ?? "")) return null;

    const panels = new Map<string, HTMLElement>();
    nav.querySelectorAll<HTMLElement>("[data-flyout-panel]").forEach((p) => {
        const id = p.dataset.flyoutPanel;
        if (id) panels.set(id, p);
    });
    if (panels.size === 0) return null;

    const initial = nav.dataset.drawerInitialFlyout ?? "";
    const openKey = initial !== "" && panels.has(initial) ? initial : null;
    return {
        nav,
        panels,
        openKey,
        persistent: nav.dataset.drawerMode === "side-expanded",
        floating: nav.dataset.drawerMode === "figma-floating",
    };
}

function findToggle(state: FlyoutState, key: string): HTMLElement | null {
    const toggles = state.nav.querySelectorAll<HTMLElement>("[data-flyout-toggle]");
    for (const t of Array.from(toggles)) {
        if (t.dataset.flyoutToggle === key) return t;
    }
    return null;
}

function setToggleExpanded(state: FlyoutState, key: string, expanded: boolean): void {
    state.nav.querySelectorAll<HTMLElement>("[data-flyout-toggle]").forEach((t) => {
        if (t.dataset.flyoutToggle === key) {
            t.setAttribute("aria-expanded", expanded ? "true" : "false");
        }
    });
}

function bindFloatingHover(state: FlyoutState): void {
    let closeTimer: number | null = null;
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const mobileDrawer = window.matchMedia("(max-width: 1024px)");
    const canHover = (): boolean => finePointer.matches && !mobileDrawer.matches;

    const clearClose = (): void => {
        if (closeTimer === null) return;
        window.clearTimeout(closeTimer);
        closeTimer = null;
    };

    const scheduleClose = (): void => {
        clearClose();
        closeTimer = window.setTimeout(() => {
            if (state.openKey) closeFlyout(state, state.openKey);
        }, 180);
    };

    state.nav.addEventListener("mouseenter", () => {
        if (canHover()) clearClose();
    });
    state.nav.addEventListener("mouseleave", () => {
        if (canHover()) scheduleClose();
    });

    state.nav.querySelectorAll<HTMLElement>("[data-flyout-toggle]").forEach((toggle) => {
        const key = toggle.dataset.flyoutToggle;
        if (!key) return;
        toggle.addEventListener("mouseenter", () => {
            if (!canHover()) return;
            clearClose();
            openFlyout(state, key, { focusFirst: false });
        });
        toggle.addEventListener("focus", () => {
            if (!canHover()) return;
            clearClose();
            openFlyout(state, key, { focusFirst: false });
        });
    });

    state.panels.forEach((panel) => {
        panel.addEventListener("mouseenter", () => {
            if (canHover()) clearClose();
        });
        panel.addEventListener("mouseleave", () => {
            if (canHover()) scheduleClose();
        });
    });

    bindFloatingSubmenus(state, canHover, clearClose);
}

function bindFloatingSubmenus(
    state: FlyoutState,
    canHover: () => boolean,
    clearDrawerClose: () => void,
): void {
    const submenuCloseTimers = new WeakMap<HTMLDetailsElement, number>();

    const clearSubmenuClose = (details: HTMLDetailsElement): void => {
        const timer = submenuCloseTimers.get(details);
        if (timer === undefined) return;
        window.clearTimeout(timer);
        submenuCloseTimers.delete(details);
    };

    const scheduleSubmenuClose = (details: HTMLDetailsElement): void => {
        clearSubmenuClose(details);
        submenuCloseTimers.set(details, window.setTimeout(() => closeFloatingSubmenu(details, true), 160));
    };

    state.panels.forEach((panel) => {
        panel.querySelectorAll<HTMLDetailsElement>("[data-flyout-submenu]").forEach((details) => {
            const trigger = details.querySelector<HTMLElement>("[data-flyout-submenu-trigger]");
            const submenuPanel = details.querySelector<HTMLElement>("[data-flyout-submenu-panel]");
            if (!trigger || !submenuPanel) return;

            details.addEventListener("mouseenter", () => {
                if (!canHover()) return;
                clearDrawerClose();
                clearSubmenuClose(details);
                openFloatingSubmenu(state, details);
            });

            details.addEventListener("mouseleave", () => {
                if (!canHover()) return;
                scheduleSubmenuClose(details);
            });

            trigger.addEventListener("focusin", () => {
                if (!canHover()) return;
                clearDrawerClose();
                clearSubmenuClose(details);
                openFloatingSubmenu(state, details);
            });

            trigger.addEventListener("click", (e) => {
                if (!canHover()) {
                    e.preventDefault();
                    clearDrawerClose();
                    clearSubmenuClose(details);
                    openMobileSubmenu(details);
                    return;
                }
                e.preventDefault();
                clearDrawerClose();
                clearSubmenuClose(details);
                if (details.hasAttribute("data-cascade-open")) {
                    closeFloatingSubmenu(details, true);
                } else {
                    openFloatingSubmenu(state, details);
                }
            });

            submenuPanel.addEventListener("mouseenter", () => {
                if (!canHover()) return;
                clearDrawerClose();
                clearSubmenuClose(details);
            });

            submenuPanel.addEventListener("click", (event) => {
                if (canHover()) return;
                const target = event.target as HTMLElement | null;
                const back = target?.closest<HTMLElement>("[data-flyout-mobile-back]");
                if (!back) return;
                event.preventDefault();
                event.stopPropagation();
                closeMobileSubmenu(details, true);
                trigger.focus({ preventScroll: true });
            });

            submenuPanel.addEventListener("mouseleave", () => {
                if (!canHover()) return;
                scheduleSubmenuClose(details);
            });

            if (details.open && canHover()) {
                window.requestAnimationFrame(() => openFloatingSubmenu(state, details));
            }
        });
    });
}

function openMobileSubmenu(details: HTMLDetailsElement): void {
    closeSiblingMobileSubmenus(details);
    details.open = true;
    details.setAttribute("data-mobile-layer-open", "");
    setMobileLayerActive(details);
    const back = details.querySelector<HTMLElement>(":scope > [data-flyout-submenu-panel] [data-flyout-mobile-back]");
    window.setTimeout(() => back?.focus({ preventScroll: true }), 120);
}

function closeSiblingMobileSubmenus(details: HTMLDetailsElement): void {
    const parentList = details.parentElement?.parentElement;
    if (!parentList) return;
    parentList.querySelectorAll<HTMLDetailsElement>(":scope > li > details[data-flyout-submenu]").forEach((sibling) => {
        if (sibling !== details) closeMobileSubmenu(sibling, true);
    });
}

function closeMobileSubmenu(details: HTMLDetailsElement, includeSelf: boolean): void {
    details.querySelectorAll<HTMLDetailsElement>("[data-flyout-submenu]").forEach((child) => {
        child.removeAttribute("data-mobile-layer-open");
        child.open = false;
    });

    if (includeSelf) {
        details.removeAttribute("data-mobile-layer-open");
        details.open = false;
    }
    setMobileLayerActive(details);
}

function setMobileLayerActive(details: HTMLDetailsElement): void {
    const panel = details.closest<HTMLElement>("[data-flyout-panel]");
    if (!panel) return;
    const active = panel.querySelector("[data-flyout-submenu][data-mobile-layer-open]") !== null;
    panel.toggleAttribute("data-mobile-layer-active", active);
}

function closeDeepestMobileSubmenu(state: FlyoutState): boolean {
    const panel = state.openKey ? state.panels.get(state.openKey) : null;
    if (!panel) return false;
    const openSubmenus = Array.from(panel.querySelectorAll<HTMLDetailsElement>("[data-flyout-submenu][data-mobile-layer-open]"));
    const deepest = openSubmenus[openSubmenus.length - 1];
    if (!deepest) return false;

    const trigger = deepest.querySelector<HTMLElement>("[data-flyout-submenu-trigger]");
    closeMobileSubmenu(deepest, true);
    trigger?.focus({ preventScroll: true });
    return true;
}

function openFlyout(state: FlyoutState, key: string, opts: { focusFirst?: boolean } = {}): void {
    if (state.openKey && state.openKey !== key) closeFlyout(state, state.openKey);
    const panel = state.panels.get(key);
    if (!panel) return;
    if (state.floating) {
        positionFloatingFlyout(state, key);
    }
    panel.setAttribute("data-flyout-open", "");
    panel.setAttribute("aria-hidden", "false");
    setToggleExpanded(state, key, true);
    state.openKey = key;
    state.nav.setAttribute("data-flyout-active", ""); // 본문 dim on
    // 슬라이드 후 첫 항목 focus
    const first = panel.querySelector<HTMLElement>("a.admin-drawer__leaf, summary, button");
    if (opts.focusFirst !== false && first) window.setTimeout(() => first.focus({ preventScroll: true }), 200);
}

function openFloatingSubmenu(state: FlyoutState, details: HTMLDetailsElement): void {
    closeSiblingFloatingSubmenus(details);
    details.open = true;
    details.setAttribute("data-cascade-open", "");
    positionFloatingSubmenu(details);

    details.querySelectorAll<HTMLDetailsElement>("[data-flyout-submenu][data-cascade-open]").forEach((child) => {
        positionFloatingSubmenu(child);
    });
}

function closeSiblingFloatingSubmenus(details: HTMLDetailsElement): void {
    const parentList = details.parentElement?.parentElement;
    if (!parentList) return;
    parentList.querySelectorAll<HTMLDetailsElement>(":scope > li > details[data-flyout-submenu]").forEach((sibling) => {
        if (sibling !== details) closeFloatingSubmenu(sibling, true);
    });
}

function closeFloatingSubmenu(details: HTMLDetailsElement, includeSelf: boolean): void {
    details.querySelectorAll<HTMLDetailsElement>("[data-flyout-submenu]").forEach((child) => {
        child.removeAttribute("data-cascade-open");
        child.removeAttribute("data-cascade-placement");
        child.removeAttribute("data-cascade-y");
        child.open = false;
        resetFloatingSubmenuPanel(child);
    });

    if (!includeSelf) return;
    details.removeAttribute("data-cascade-open");
    details.removeAttribute("data-cascade-placement");
    details.removeAttribute("data-cascade-y");
    details.open = false;
    resetFloatingSubmenuPanel(details);
}

function resetFloatingSubmenuPanel(details: HTMLDetailsElement): void {
    const panel = details.querySelector<HTMLElement>("[data-flyout-submenu-panel]");
    if (!panel) return;
    panel.style.removeProperty("--admin-cascade-menu-top");
    panel.style.removeProperty("--admin-cascade-menu-left");
    panel.style.removeProperty("--admin-cascade-menu-width");
    panel.style.removeProperty("--admin-cascade-menu-max-height");
}

function closeDeepestFloatingSubmenu(state: FlyoutState): boolean {
    const panel = state.openKey ? state.panels.get(state.openKey) : null;
    if (!panel) return false;
    const openSubmenus = Array.from(panel.querySelectorAll<HTMLDetailsElement>("[data-flyout-submenu][data-cascade-open]"));
    const deepest = openSubmenus[openSubmenus.length - 1];
    if (!deepest) return false;

    closeFloatingSubmenu(deepest, true);
    deepest.querySelector<HTMLElement>("[data-flyout-submenu-trigger]")?.focus({ preventScroll: true });
    return true;
}

function positionOpenFloatingSubmenus(state: FlyoutState): void {
    const panel = state.openKey ? state.panels.get(state.openKey) : null;
    if (!panel) return;
    panel.querySelectorAll<HTMLDetailsElement>("[data-flyout-submenu][data-cascade-open]").forEach((details) => {
        positionFloatingSubmenu(details);
    });
}

function positionFloatingSubmenu(details: HTMLDetailsElement): void {
    const trigger = details.querySelector<HTMLElement>("[data-flyout-submenu-trigger]");
    const panel = details.querySelector<HTMLElement>("[data-flyout-submenu-panel]");
    if (!trigger || !panel) return;

    const gutter = 8;
    const minPanelWidth = 240;
    const minPanelHeight = 160;
    const triggerRect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxPanelWidth = Math.max(minPanelWidth, viewportWidth - (gutter * 2));
    const rawPanelWidth = panel.offsetWidth || 296;
    const panelWidth = Math.min(rawPanelWidth, maxPanelWidth);

    const rightLeft = triggerRect.right + gutter;
    const leftLeft = triggerRect.left - panelWidth - gutter;
    let left = rightLeft;
    let placement = "right";
    let drilldown = false;

    if (rightLeft + panelWidth > viewportWidth - gutter && leftLeft >= gutter) {
        left = leftLeft;
        placement = "left";
    } else if (rightLeft + panelWidth > viewportWidth - gutter) {
        const availableRight = Math.max(0, viewportWidth - rightLeft - gutter);
        const availableLeft = Math.max(0, triggerRect.left - gutter);
        drilldown = Math.max(availableRight, availableLeft) < minPanelWidth;
        if (drilldown) {
            const flyoutPanel = details.closest<HTMLElement>("[data-flyout-panel]");
            const flyoutRect = flyoutPanel?.getBoundingClientRect();
            const fallbackWidth = flyoutRect ? Math.min(flyoutRect.width, viewportWidth - (gutter * 2)) : panelWidth;
            left = flyoutRect ? Math.min(Math.max(gutter, flyoutRect.left), viewportWidth - fallbackWidth - gutter) : gutter;
            placement = "drilldown";
        } else {
            left = Math.min(Math.max(gutter, rightLeft), viewportWidth - panelWidth - gutter);
            placement = "clamped";
        }
    }

    const naturalHeight = panel.scrollHeight || 320;
    let top = triggerRect.top;
    let maxHeight = Math.max(minPanelHeight, viewportHeight - top - gutter);
    let yPlacement = "down";

    if (drilldown) {
        const flyoutPanel = details.closest<HTMLElement>("[data-flyout-panel]");
        const flyoutRect = flyoutPanel?.getBoundingClientRect();
        top = flyoutRect ? Math.max(gutter, flyoutRect.top) : gutter;
        maxHeight = Math.max(minPanelHeight, viewportHeight - top - gutter);
    } else {
        const availableBelow = viewportHeight - triggerRect.top - gutter;
        const availableAbove = triggerRect.bottom - gutter;
        if (availableBelow >= minPanelHeight) {
            top = Math.max(gutter, triggerRect.top);
            maxHeight = Math.max(minPanelHeight, availableBelow);
        } else if (availableAbove > availableBelow) {
            const upwardHeight = Math.min(naturalHeight, Math.max(minPanelHeight, availableAbove));
            top = Math.max(gutter, triggerRect.bottom - upwardHeight);
            maxHeight = Math.max(minPanelHeight, availableAbove);
            yPlacement = "up";
        } else {
            top = gutter;
            maxHeight = Math.max(minPanelHeight, viewportHeight - (gutter * 2));
        }
    }

    const appliedWidth = placement === "drilldown"
        ? Math.min(panelWidth, viewportWidth - (gutter * 2), details.closest<HTMLElement>("[data-flyout-panel]")?.getBoundingClientRect().width ?? panelWidth)
        : panelWidth;

    details.setAttribute("data-cascade-placement", placement);
    details.setAttribute("data-cascade-y", yPlacement);
    panel.style.setProperty("--admin-cascade-menu-top", `${top}px`);
    panel.style.setProperty("--admin-cascade-menu-left", `${left}px`);
    panel.style.setProperty("--admin-cascade-menu-width", `${appliedWidth}px`);
    panel.style.setProperty("--admin-cascade-menu-max-height", `${maxHeight}px`);
}

function positionFloatingFlyout(state: FlyoutState, key: string): void {
    const panel = state.panels.get(key);
    const toggle = findToggle(state, key);
    if (!panel || !toggle) return;

    const navRect = state.nav.getBoundingClientRect();
    const toggleRect = toggle.getBoundingClientRect();
    const panelHeight = panel.offsetHeight || 360;
    const gutter = 8;
    const minTop = gutter;
    const maxTop = Math.max(minTop, window.innerHeight - panelHeight - gutter);
    const preferredTop = toggleRect.top - navRect.top;
    const top = Math.min(Math.max(preferredTop, minTop), maxTop);

    panel.style.setProperty("--admin-flyout-y", `${top}px`);
}

function closeFlyout(state: FlyoutState, key: string): void {
    const panel = state.panels.get(key);
    if (panel) {
        if (state.floating) {
            panel.querySelectorAll<HTMLDetailsElement>("[data-flyout-submenu][data-cascade-open]").forEach((details) => {
                closeFloatingSubmenu(details, true);
            });
            panel.querySelectorAll<HTMLDetailsElement>("[data-flyout-submenu][data-mobile-layer-open]").forEach((details) => {
                closeMobileSubmenu(details, true);
            });
        }
        panel.removeAttribute("data-flyout-open");
        panel.removeAttribute("data-mobile-layer-active");
        panel.setAttribute("aria-hidden", "true");
    }
    setToggleExpanded(state, key, false);
    state.nav.removeAttribute("data-flyout-active"); // 본문 dim off (패널 전환 시 openFlyout 가 곧바로 재설정)
    if (state.openKey === key) state.openKey = null;
}

function toggleFlyout(state: FlyoutState, key: string): void {
    if (state.openKey === key) closeFlyout(state, key);
    else openFlyout(state, key);
}
